import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"

import { Api } from "api"
import { SectionBox } from "components/AppLayout/PageContainer"
import { Button } from "components/Button"
import { TagList } from "components/Tag"
import { selectAllSelected } from "store/newsUI"
import { GroupedData } from "types/Newsletter"
import { PKD } from "types/Template"
import usePrefix from "utils/usePrefix"

interface GroupedClientsBySelectedNewsProps {
  confirmAndSetNextStep: () => void
}

const defaultGroupedData: GroupedData = {
  tags: [],
  customTags: [],
  activityFormCodes: [],
  pkdCodes: [],
  logicalAlternativeCustomTags: [],
  logicalAlternativePkds: [],
  logicalAlternativeTags: [],
  receiverCount: 0
}

const getUniqueValues = <T, K extends keyof T>(items: T[], key: K) =>
  Array.from(new Set(items.flatMap((item) => item[key]))).filter(
    Boolean
  ) as any[]

export const removePkdDuplicates = (array: PKD[]) => {
  const map = new Map()
  return array.filter((item) => {
    if (item?.value && item?.version) {
      const key = `${item.value}-${item.version}`
      if (!map.has(key)) {
        map.set(key, true)
        return true
      }
    }
    return false
  })
}

export const GroupedClientsBySelectedNews: React.FC<
  GroupedClientsBySelectedNewsProps
> = ({ confirmAndSetNextStep }) => {
  const t = usePrefix("Bulletin")
  const selected = useSelector(selectAllSelected)
  const [{ receiverCount, ...groupedTagsData }, setGroupedData] =
    useState<GroupedData>(defaultGroupedData)

  useEffect(() => {
    const fetchGroupedData = async () => {
      if (selected.length === 0) return setGroupedData(defaultGroupedData)

      try {
        const res = await Api.getPreviewNewsletters(selected, null)
        const newsletters = res.result.newsletters

        setGroupedData({
          tags: getUniqueValues(newsletters, "tags"),
          customTags: getUniqueValues(newsletters, "customTags"),
          activityFormCodes: getUniqueValues(newsletters, "activityFormCode"),
          pkdCodes: removePkdDuplicates(newsletters.flatMap((n) => n.pkdCodes)),
          logicalAlternativeCustomTags: getUniqueValues(
            newsletters,
            "logicalAlternativeCustomTags"
          ),
          logicalAlternativePkds: removePkdDuplicates(
            newsletters.flatMap((n) => n.logicalAlternativePkdCodes)
          ),
          logicalAlternativeTags: getUniqueValues(
            newsletters,
            "logicalAlternativeTags"
          ),
          receiverCount: res.result.receiversCount
        })
      } catch (err) {
        console.error("Error fetching grouped data:", err)
      }
    }

    fetchGroupedData()
  }, [selected])

  return (
    <div className="mt-3">
      <h4>{t("news_clients_header")}</h4>
      <SectionBox className="mb-4 min-h-[121px] w-full p-4">
        {receiverCount === 0 ? (
          <div className="flex h-full items-center justify-center">
            <span className="w-full text-sm text-muted-foreground lg:w-[300px]">
              {t("news_clients_helper_empty")}
            </span>
          </div>
        ) : (
          <div className="flex max-h-[420px] w-full flex-col gap-2 overflow-auto xl:w-[300px]">
            <TagList {...groupedTagsData} />
            <span>
              {receiverCount}{" "}
              {t("receiver_interval", {
                postProcess: "interval",
                count: receiverCount
              })}
            </span>
          </div>
        )}
      </SectionBox>
      <Button
        variant="alternative"
        onClick={confirmAndSetNextStep}
        disabled={receiverCount === 0}
        style={{ width: "100%" }}
        data-gtm="gtm-newsletter-send-next"
      >
        {t("news_cta_label")}
      </Button>
    </div>
  )
}
